import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { Storage } from '../studioStorage.js'

// OperatorProfileContext (Wave 47t)
//
// Holds the operator's identity surface: avatar (image as a small
// data: URL), display name, role line, and four social media URLs
// (X, LinkedIn, GitHub, Substack). Persisted to browser-local
// storage under STORAGE_KEY so it survives reloads. Future wave
// can promote this to a server-side `operator_profile` table; the
// shape here is forward-compatible.
//
// Avatar is stored as a JPEG dataURL after canvas-resize to 256×256
// (see OperatorProfilePanel). At that resolution + 0.85 quality
// the dataURL is typically 15–40KB — well under any localStorage
// quota.

const STORAGE_KEY = 'studio.operator-profile'

const DEFAULT_PROFILE = {
  name: 'Ignazio De Santis',
  role: 'AI Engineer · RAG, LLM agents',
  avatarDataUrl: '',
  xUrl: '',
  linkedinUrl: '',
  githubUrl: '',
  substackUrl: '',
}

function loadInitial() {
  const stored = Storage.get(STORAGE_KEY)
  if (!stored || typeof stored !== 'object') return DEFAULT_PROFILE
  return { ...DEFAULT_PROFILE, ...stored }
}

const OperatorProfileContext = createContext({
  profile: DEFAULT_PROFILE,
  updateProfile: () => {},
  clearAvatar: () => {},
})

export function OperatorProfileProvider({ children }) {
  const [profile, setProfile] = useState(loadInitial)

  const updateProfile = useCallback((next) => {
    setProfile((current) => {
      const merged =
        typeof next === 'function' ? next(current) : { ...current, ...next }
      Storage.set(STORAGE_KEY, merged)
      return merged
    })
  }, [])

  const clearAvatar = useCallback(() => {
    updateProfile({ avatarDataUrl: '' })
  }, [updateProfile])

  const value = useMemo(
    () => ({ profile, updateProfile, clearAvatar }),
    [profile, updateProfile, clearAvatar]
  )

  return (
    <OperatorProfileContext.Provider value={value}>
      {children}
    </OperatorProfileContext.Provider>
  )
}

export function useOperatorProfile() {
  return useContext(OperatorProfileContext)
}

export { OperatorProfileContext, DEFAULT_PROFILE }
